import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../config';

/**
 * FitnessGoalSetup - Weekly workout scheduling wizard
 * 
 * Flow:
 * 1. Goal Name & Level Selection (Beginner/Intermediate/Advanced)
 * 2. Weekly Schedule Builder (assign activities to days)
 * 3. Confirmation & Creation
 */

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = {
  monday: 'Mon',
  tuesday: 'Tue', 
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun'
};

const SUGGESTED_ACTIVITIES = [
  '🚴 Indoor Biking',
  '🏊 Swimming',
  '🏋️ Weight Training',
  '🏃 Running',
  '🧘 Yoga',
  '🚶 Walking',
  '🤸 HIIT',
  '🚲 Outdoor Cycling',
  '🏠 Home Workout',
  '💪 Strength Training',
  '🧗 Rock Climbing',
  '🎾 Tennis/Sports',
];

const LEVEL_INFO = {
  beginner: {
    emoji: '🌱',
    title: 'Beginner',
    description: 'Just starting out. Focus on building the habit.',
    suggestion: '2-3 workout days per week with rest days between'
  },
  intermediate: {
    emoji: '🌿',
    title: 'Intermediate',
    description: 'Some experience. Ready for more consistency.',
    suggestion: '4-5 workout days per week'
  },
  advanced: {
    emoji: '🌳',
    title: 'Advanced',
    description: 'Experienced athlete. Already disciplined.',
    suggestion: '5-6 workout days per week with planned rest'
  }
};

const FitnessGoalSetup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [goalName, setGoalName] = useState('');
  const [level, setLevel] = useState('');
  const [schedule, setSchedule] = useState({
    monday: { activity: '', is_rest_day: false },
    tuesday: { activity: '', is_rest_day: false },
    wednesday: { activity: '', is_rest_day: false },
    thursday: { activity: '', is_rest_day: false },
    friday: { activity: '', is_rest_day: false },
    saturday: { activity: '', is_rest_day: false },
    sunday: { activity: '', is_rest_day: false }
  });
  const [customActivity, setCustomActivity] = useState('');

  const updateDaySchedule = (day, activity, isRest = false) => {
    setSchedule(prev => ({
      ...prev,
      [day]: { activity: isRest ? 'Rest' : activity, is_rest_day: isRest }
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/fitness`, {
        goal_name: goalName,
        level,
        schedule,
        reminder_time: '08:00' // Default, can be changed in settings
      });
      
      // Show success and navigate
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to create fitness goal:', err);
      alert(err.response?.data?.detail || 'Failed to create fitness goal');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedStep1 = goalName.trim() && level;
  const canProceedStep2 = DAYS.every(day => 
    schedule[day].is_rest_day || schedule[day].activity.trim()
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">🏋️ Create Fitness Goal</h1>
          <p className="text-sm text-gray-500 mt-1">Step {step} of 3</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-gray-200 h-2">
        <div 
          className="bg-blue-500 h-full transition-all duration-500"
          style={{ width: `${(step / 3) * 100}%` }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg">
          
          {/* Step 1: Goal Name & Level */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <span className="text-4xl">🎯</span>
                <h2 className="text-2xl font-bold text-gray-900 mt-3">Name Your Fitness Goal</h2>
                <p className="text-gray-500 mt-2">What are you working toward?</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Goal Name
                </label>
                <input
                  type="text"
                  value={goalName}
                  onChange={(e) => setGoalName(e.target.value)}
                  placeholder="e.g., Get Fit, Build Strength, Train for 5K"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Your Fitness Level
                </label>
                <div className="space-y-3">
                  {Object.entries(LEVEL_INFO).map(([key, info]) => (
                    <button
                      key={key}
                      onClick={() => setLevel(key)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition ${
                        level === key 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{info.emoji}</span>
                        <div>
                          <div className="font-semibold text-gray-900">{info.title}</div>
                          <div className="text-sm text-gray-500">{info.description}</div>
                          <div className="text-xs text-blue-600 mt-1">{info.suggestion}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Weekly Schedule */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-4">
                <span className="text-4xl">📅</span>
                <h2 className="text-2xl font-bold text-gray-900 mt-3">Build Your Week</h2>
                <p className="text-gray-500 mt-2">Assign activities to each day</p>
              </div>

              {/* Quick Activity Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick Add Activities
                </label>
                <div className="flex flex-wrap gap-2 mb-4">
                  {SUGGESTED_ACTIVITIES.slice(0, 8).map(activity => (
                    <button
                      key={activity}
                      onClick={() => setCustomActivity(activity)}
                      className={`px-3 py-1.5 rounded-full text-sm transition ${
                        customActivity === activity
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {activity}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={customActivity}
                  onChange={(e) => setCustomActivity(e.target.value)}
                  placeholder="Or type custom activity..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              {/* Weekly Schedule Grid */}
              <div className="space-y-2">
                {DAYS.map(day => (
                  <div key={day} className="flex items-center gap-2">
                    <div className="w-12 text-sm font-medium text-gray-600">
                      {DAY_LABELS[day]}
                    </div>
                    <div className="flex-1">
                      {schedule[day].is_rest_day ? (
                        <div className="px-3 py-2 bg-gray-100 rounded-lg text-gray-500 text-sm">
                          😴 Rest Day
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={schedule[day].activity}
                          onChange={(e) => updateDaySchedule(day, e.target.value)}
                          onFocus={() => {
                            if (customActivity && !schedule[day].activity) {
                              updateDaySchedule(day, customActivity);
                            }
                          }}
                          placeholder="Tap to assign activity"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        />
                      )}
                    </div>
                    <button
                      onClick={() => updateDaySchedule(day, '', !schedule[day].is_rest_day)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition ${
                        schedule[day].is_rest_day
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Rest
                    </button>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <strong>💡 Tip:</strong> Rest days are important for recovery. 
                {level === 'beginner' && ' As a beginner, aim for 3-4 rest days.'}
                {level === 'intermediate' && ' At your level, 2-3 rest days work well.'}
                {level === 'advanced' && ' Even advanced athletes need 1-2 rest days.'}
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-4">
                <span className="text-4xl">✨</span>
                <h2 className="text-2xl font-bold text-gray-900 mt-3">Review Your Plan</h2>
                <p className="text-gray-500 mt-2">Here&apos;s your weekly fitness schedule</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">{LEVEL_INFO[level]?.emoji}</span>
                  <div>
                    <div className="font-semibold text-gray-900">{goalName}</div>
                    <div className="text-sm text-gray-500">{LEVEL_INFO[level]?.title} Level</div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-3 space-y-2">
                  {DAYS.map(day => (
                    <div key={day} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 capitalize">{day}</span>
                      <span className={schedule[day].is_rest_day ? 'text-gray-400' : 'text-gray-900 font-medium'}>
                        {schedule[day].is_rest_day ? '😴 Rest' : schedule[day].activity}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <span className="text-xl">⏰</span>
                  <div>
                    <p className="font-medium text-blue-800">Daily Reminders</p>
                    <p className="text-sm text-blue-700 mt-1">
                      You&apos;ll receive reminders for each day&apos;s scheduled activity. 
                      Customize the time in <strong>Settings</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-8">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
              >
                ← Back
              </button>
            )}
            
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
                className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition disabled:opacity-50"
              >
                {submitting ? 'Creating...' : '🚀 Start My Fitness Journey'}
              </button>
            )}
          </div>

          {step === 1 && (
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full mt-3 text-gray-500 hover:text-gray-700 text-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FitnessGoalSetup;
